// @flow
export const SEARCHPARAMS_SET = 'SEARCH_PARAMS/SET';
export const SEARCHPARAMS_SYNC_INPUT_PARAMS = 'SEARCH_PARAMS/SYNC_INPUT_PARAMS';
export const SEARCHPARAMS_UPDATE_INPUT_PARAMS_REQUEST =
    'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_REQUEST';
export const SEARCHPARAMS_UPDATE_INPUT_PARAMS_COMPLETE =
    'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_COMPLETE';
export const SEARCHPARAMS_RESET_INPUT_PARAMS =
    'SEARCH_PARAMS/RESET_INPUT_PARAMS';
export const SEARCHPARAMS_UPDATE_PRICE_INPUT_VALUES_VALIDITY =
    'SEARCH_PARAMS/UPDATE_PRICE_INPUT_VALUES_VALIDITY';

export type ActionType =
    | 'SEARCH_PARAMS/SET'
    | 'SEARCH_PARAMS/SYNC_INPUT_PARAMS'
    | 'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_REQUEST'
    | 'SEARCH_PARAMS/UPDATE_INPUT_PARAMS_COMPLETE'
    | 'SEARCH_PARAMS/RESET_INPUT_PARAMS'
    | 'SEARCH_PARAMS/UPDATE_PRICE_INPUT_VALUES_VALIDITY';
export type Action = {
    type: ActionType,
    payload: any
};

export type FilterCheckType = {checked: boolean, name: string};

import {newArrayFromFilters} from '../utils/filters';
import {syncPets, SYNC_STRATEGY_FILTER} from '@homeaway/search-url-parser';

import type {State as ReduxState} from '../reducers/index';
import type {SearchParam, SearchParamsType} from '../reducers/searchParams';
import {
    INSTANT_BOOK_FILTER_ID,
    PETS_INCLUDED_FILTER_ID
} from '../constants/filters';

let setDebouncedFiltersTimeout = null;

const dispatchUpdateInputParams = (
    params: SearchParamsType,
    state: ReduxState,
    dispatch: (Action) => any
) => {
    // If filter is undefined (not set), the current
    // filters will be preserved. Otherwise they'll be
    // overridden with the filters in payload.
    const filter =
        typeof params.filter === 'undefined'
            ? newArrayFromFilters(state.searchParams.inputParams)
            : // ensure `filter` property is re-written
              // unless explictitly `undefined`.
              newArrayFromFilters(params);

    // sync petIncluded with pet filter
    const newParams = syncPets({...params, filter}, SYNC_STRATEGY_FILTER);

    dispatch({
        type: SEARCHPARAMS_UPDATE_INPUT_PARAMS_REQUEST,
        payload: newParams
    });

    if (setDebouncedFiltersTimeout) {
        clearTimeout(setDebouncedFiltersTimeout);
    }

    setDebouncedFiltersTimeout = setTimeout(() => {
        dispatch({
            type: SEARCHPARAMS_UPDATE_INPUT_PARAMS_COMPLETE,
            payload: {}
        });
    }, 200);
};

const ACTION_CREATORS = {
    /**
     * setSearchParams()
     * This enforce search params due to conditions and set `inputParams``
     */
    setSearchParams(payload: {pathname: string, params: any}) {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            const {
                system: {currency}
            } = getState();

            dispatch({
                type: SEARCHPARAMS_SET,
                payload: {
                    ...payload,
                    currency
                }
            });
        };
    },

    /**
     * syncInputParams()
     * This syncs our `inputParams` by copying our current `params` into `inputParams`
     */
    syncInputParams(response: SearchParamsType = {}): Function {
        return (dispatch: (Action) => any) => {
            dispatch({
                type: SEARCHPARAMS_SYNC_INPUT_PARAMS,
                payload: response
            });
        };
    },

    /**
     * resetInputParams()
     * This will reset inputParams to an empty default state.
     */
    resetInputParams(payload?: SearchParam | SearchParam[]): Function {
        return (dispatch: (Action) => any) => {
            dispatch({
                type: SEARCHPARAMS_RESET_INPUT_PARAMS,
                payload
            });
        };
    },

    /**
     * updateInputParams()
     * This mutates `inputParams`, which will cause apollo query side effects in FilterForm.js
     * and propagate state anywhere else used.
     */
    updateInputParams(params: SearchParamsType): Function {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            dispatchUpdateInputParams(params, getState(), dispatch);
        };
    },

    /**
     * updateFilterCheck()
     * This mutates `inputParams.filter`, which will cause apollo query side effects in FilterForm.js
     * and propagate state anywhere else used.
     */
    updateFilterCheck(updatedFilter: FilterCheckType) {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            const {filter} = getState().searchParams.inputParams;
            const filterArray: string[] = [].concat(filter || []);
            const newParams: SearchParamsType = {
                filter: filterArray
            };

            if (updatedFilter.checked) {
                filterArray.push(updatedFilter.name);
            } else {
                const elementIndex = filterArray.indexOf(updatedFilter.name);
                if (elementIndex >= 0) {
                    filterArray.splice(elementIndex, 1);
                }
            }

            dispatchUpdateInputParams(newParams, getState(), dispatch);
        };
    },

    /**
     * clearFilters()
     * This mutates `inputParams.filter`, which will cause apollo query side effects in FilterForm.js
     * and propagate state anywhere else used.
     */
    clearFilters(filtersToClear: string[]) {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            const {filter} = getState().searchParams.inputParams;
            const filterArray: string[] = [].concat(filter || []);
            const newParams: SearchParamsType = {
                filter: filterArray
            };

            filtersToClear.forEach((filterToClear) => {
                const elementIndex = filterArray.indexOf(filterToClear);
                if (elementIndex >= 0) {
                    filterArray.splice(elementIndex, 1);
                }
            });

            dispatchUpdateInputParams(newParams, getState(), dispatch);
            dispatch({
                type: SEARCHPARAMS_RESET_INPUT_PARAMS,
                payload: filtersToClear
            });
        };
    },

    /**
     * setFilterFromTypeahead()
     * This mutates `inputParams.filter`, which will cause apollo query side effects in FilterForm.js
     * and propagate state anywhere else used.
     **/
    setFilterFromTypeahead(typeaheadFilter: string) {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            const {filter} = getState().searchParams.inputParams;
            const newFilter: string[] = [typeaheadFilter];

            // these two filters should not be reset because they are included in guest-picker and filter bar
            if (filter && filter.indexOf(PETS_INCLUDED_FILTER_ID) > -1) {
                newFilter.push(PETS_INCLUDED_FILTER_ID);
            }
            if (filter && filter.indexOf(INSTANT_BOOK_FILTER_ID) > -1) {
                newFilter.push(INSTANT_BOOK_FILTER_ID);
            }

            const newParams: SearchParamsType = {
                filter: newFilter
            };
            dispatchUpdateInputParams(newParams, getState(), dispatch);
        };
    },

    updatePriceInputValuesValidity(areValid: boolean): Action {
        return {
            type: SEARCHPARAMS_UPDATE_PRICE_INPUT_VALUES_VALIDITY,
            payload: {areValid}
        };
    },

    updateMultipleFilterCheck(updatedFilters: FilterCheckType[]) {
        return (dispatch: (Action) => any, getState: (void) => ReduxState) => {
            const {filter} = getState().searchParams.inputParams;
            const filterArray: string[] = [].concat(filter || []);
            const newParams: SearchParamsType = {
                filter: filterArray
            };
            updatedFilters.forEach((submittedFilter) => {
                if (submittedFilter.checked) {
                    if (!filterArray.includes(submittedFilter.name)) {
                        filterArray.push(submittedFilter.name);
                    }
                } else {
                    const elementIndex = filterArray.indexOf(
                        submittedFilter.name
                    );
                    if (elementIndex >= 0) {
                        filterArray.splice(elementIndex, 1);
                    }
                }
            });
            dispatchUpdateInputParams(newParams, getState(), dispatch);
        };
    }
};

export default {
    ...ACTION_CREATORS
};
